import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { authService } from '../services/auth.service'
import { permissionsService } from '../services/permissions.service'
import { settingsService } from '../services/settings.service'
import { toastService } from '../services/toast.service'
import { useAppStore } from '../store/useAppStore'

export const useAuth = () => {
  const navigate = useNavigate()
  const { isAuthenticated, setAuthenticated, setUiSettings, user, logout: storeLogout } = useAppStore()

  useEffect(() => {
    if (window.location.pathname.startsWith('/login')) {
      if (isAuthenticated === true) {
        navigate('/dashboard', { replace: true })
      } else {
        setAuthenticated(false)
      }
      return
    }

    if (isAuthenticated === false) {
      navigate('/login', { replace: true })
      return
    }

    const checkAuth = async () => {
      try {
        const uiSettings = await settingsService.initializeSettings()
        if (!uiSettings.user || !uiSettings.user.id) {
          setAuthenticated(false)
          navigate('/login', { replace: true })
          return
        }
        setUiSettings(uiSettings)
        permissionsService.initialize(uiSettings.permissions, uiSettings.user)
        setAuthenticated(true)
      } catch (error: any) {
        setAuthenticated(false)
        navigate('/login', { replace: true })
      }
    }

    if (isAuthenticated === null) {
      checkAuth()
    } else if (isAuthenticated === true) {
      checkAuth()
    }


  }, [isAuthenticated, setAuthenticated, setUiSettings, navigate, storeLogout])

  const login = async (username: string, password: string, mfaCode?: string) => {
    try {
      const loginResponse = await authService.login(username, password, mfaCode)
      if (loginResponse.organization_selection_required) {
        return {
          organization_selection_required: true,
          organizations: loginResponse.organizations || [],
        }
      }
      const uiSettings = await settingsService.initializeSettings(true)
      setUiSettings(uiSettings)
      permissionsService.initialize(uiSettings.permissions, uiSettings.user)
      setAuthenticated(true)
      toastService.clearToasts()
      toastService.showSuccess('Uspešno ste se prijavili!')
      return true
    } catch (error: any) {
      if (error.mfa_required) {
        toastService.showError('Potreban je MFA kod')
        return 'mfa_required'
      }

      const errorMessage = error.response?.data?.non_field_errors?.[0] || error.response?.data?.detail || 'Greška pri prijavljivanju'

      if (errorMessage.includes('Invalid MFA code')) {
        toastService.showError('Neispravan MFA kod')
        return { success: false }
      } else {
        toastService.showError(errorMessage)
        return { success: false }
      }
    }
  }

  const logout = async () => {
    storeLogout()
    navigate('/login', { replace: true })
    try {
      await authService.logout()
      toastService.showSuccess('Uspešno ste se odjavili')
    } catch (error) {}
  }

  const completeOrganizationSelection = async (organizationId: number) => {
    await authService.setCurrentOrganization(organizationId)
    const uiSettings = await settingsService.initializeSettings(true)
    setUiSettings(uiSettings)
    permissionsService.initialize(uiSettings.permissions, uiSettings.user)
    setAuthenticated(true)
    toastService.clearToasts()
    toastService.showSuccess('Uspešno ste se prijavili!')
    return true
  }

  return {
    isAuthenticated,
    user,
    login,
    completeOrganizationSelection,
    logout,
  }
}
